import { useI18n } from "react-simple-i18n";
import { useEffect, useState } from "react";
import { forwardRef, useImperativeHandle } from "react";

import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import { SecondOutlineButton } from "../buttons/buttons";
import { TextField } from "./textfield";

export interface AddItemRefProps {
	validateItem: () => boolean
}

interface AddItemProps {
	label: string
	value: string[]
	onChange: (items: string[]) => void
}

interface ItemObject {
	value: string
	errorValue: boolean
}

const defaultItem: ItemObject = {
	value: "",
	errorValue: false
}

const AddItem = forwardRef(({ label, value, onChange }: AddItemProps, ref) => {
	const { t } = useI18n();
	const [items, setitems] = useState<ItemObject[]>([defaultItem]);
	const erroritem = items.find((item: ItemObject) => item.errorValue);

	useEffect(() => {
		if (value.length === 0) return;
		const newItems = value.reduce((datas: ItemObject[], data: string) => {
			return [...datas, { value: data, errorValue: false }]
		}, [])

		setitems(newItems);
	}, [value])

	useImperativeHandle(ref, () => ({
		validateItem() {
			let validateStatus = true;

			const newItems = items.reduce((datas: ItemObject[], data: ItemObject) => {
				if (!data.value) {
					validateStatus = false;
				}

				const newData: ItemObject = {
					value: data.value,
					errorValue: !data.value
				}

				return [...datas, newData]
			}, [])

			setitems(newItems);

			return validateStatus;
		}
	}))

	const addNewitem = () => {
		onChange([...value, ""]);
	}

	const deleteItem = (index: number) => () => {
		if (items.length === 1) {
			return false;
		}

		const itemValues = items.reduce((datas: string[], data: ItemObject, key: number) => {
			if (key === index) return datas;
			else return [...datas, data.value];
		}, [])

		onChange(itemValues);
	}

	const onChangeValue = (index: number) => ({ target }: any) => {
		const itemValues = items.map((data: ItemObject, key: number) => {
			if (key === index) return target.value;
			else return data.value;
		})

		onChange(itemValues);
	}

	return (
		<AddItemWrapper error={erroritem ? "true" : ""}>
			<Typography variant="subtitle1">
				{label}
			</Typography>

			{items.map((item: ItemObject, key: number) => (
				<ItemContainer key={key}>
					<TextField value={item.value}
						onChange={onChangeValue(key)}
						error={item.errorValue}
					/>

					{items.length > 1 && (
						<DeleteButton onClick={deleteItem(key)}>
							<DeleteForeverOutlinedIcon fontSize="small" />
						</DeleteButton>
					)}
				</ItemContainer>
			))}

			<SecondOutlineButton onClick={addNewitem}>
				<AddCircleOutlineIcon fontSize="small" />
				<Typography variant="body2">{t("buttons.addNew")}</Typography>
			</SecondOutlineButton>
		</AddItemWrapper>
	)
})

const AddItemWrapper = styled(Box)<{ error: string }>(({ theme, error = "" }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),

	h6: {
		paddingLeft: theme.spacing(1),
		color: !!error ? theme.palette.common.fourthColor : theme.palette.common.firstColor,
	}
}))

const ItemContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: theme.spacing(1),
}))

const DeleteButton = styled(Box)(({ theme }) => ({
	height: '100%',
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	cursor: "pointer",

	svg: {
		transition: 'transform 0.2s',
		color: theme.palette.common.fourthColor,
	},

	"&:hover": {
		svg: {
			transform: "scale(1.1)",
		}
	}
}))

export { AddItem }